import { Directive, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UiComponent } from '@df/ui/ui.component';
import { isSameDay } from '@df/util/fn/is-same-day';
import { isValidDate } from '@df/util/fn/is-valid-date';
import defaults from 'lodash-es/defaults';
import groupBy from 'lodash-es/groupBy';
import head from 'lodash-es/head';
import last from 'lodash-es/last';
import range from 'lodash-es/range';
import values from 'lodash-es/values';
import { BehaviorSubject } from 'rxjs';

/**
 * Component options interface
 */
export interface ICalendarOptions {
    firstDayOfWeek?: number; // 0 for saturday, 1 for monday
    min?: string | Date;
    max?: string | Date;
    validate?: (day: Date | string) => boolean;
    displayOtherMonthsDates?: boolean;
}

@Directive()
export abstract class Calendar2ComponentDef extends UiComponent implements OnInit, OnChanges {
    /**
     * Date model
     */
    @Input()
    get model(): string | undefined {
        const date = this.dateModel;
        return !date || this.isDisabled(date) ? undefined : this.dateToString(date);
    }
    set model(value: string | undefined) {
        this.setModelAsDate(this.stringToDate(value));
    }

    /**
     * Date change
     */
    @Output()
    modelChange = new EventEmitter<string | undefined>();

    /**
     * Component options
     * @type {IOptions}
     */
    @Input()
    options: ICalendarOptions = {};

    /**
     * All days in month (same as in weeks but not grouped)
     * @type {Date[]}
     */
    protected days!: Date[];

    /**
     * Dates (this.days) grouped in weeks
     */
    protected weeks!: (Date | null)[][];

    /**
     * Sample days to present week days row
     * @type {Date[]}
     */
    protected weekDays!: Date[];
    /**
     * Currently displayed month representation date
     * (we dont care about day, but month and year are for currently viewed thing)
     * @type {Date}
     */
    protected _monthDate!: Date;

    /**
     * Component default options
     * @type {IOptions}
     */
    private defaultOptions: ICalendarOptions = {
        firstDayOfWeek: 0
    };

    /**
     * Current date
     */
    private today = new Date();

    private minDate: Date | undefined;
    private maxDate: Date | undefined;

    /**
     * Prevent emitting changes events while applying model from input
     */
    private preventModelEmit = false;
    /**
     * Date representaiton for model
     */
    private dateModel: Date | undefined;

    /**
     * Init hook
     */
    override ngOnInit() {
        this.sanitizeOptions();
        this.setWeekDays();
        super.ngOnInit();
        this.resetMonthDate();
    }

    /**
     * Data change hook
     */
    override ngOnChanges(changes) {
        this.preventModelEmit = true;
        if (this.initiated) {
            if (changes.options) {
                this.sanitizeOptions();
                this.setWeekDays();
            }
            if (changes.model) {
                this.resetMonthDate();
            }
            this.applySelection();
        }
        this.preventModelEmit = false;
        super.ngOnChanges(changes);
    }

    /**
     * Year setter
     */
    protected set year(year: number) {
        if (this.monthDate.getFullYear() !== year) {
            this.monthDate = this.toDate(year, (this._monthDate || new Date()).getMonth(), 1);
        }
    }

    /**
     * Year getter
     */
    protected get year(): number {
        return this.monthDate.getFullYear();
    }

    protected set monthDate(monthDate: Date) {
        if (this._monthDate !== monthDate) {
            this._monthDate = monthDate;
            this.days = this.getDaysInMonth(this.monthDate.getMonth(), this.monthDate.getFullYear());
            this.weeks = this.getWeeks();
        }
    }

    protected get monthDate(): Date {
        return this._monthDate;
    }

    /**
     * Shifts calendar one month ahead
     */
    nextMonth() {
        let month = this._monthDate.getMonth() + 1;
        let year = this._monthDate.getFullYear();
        if (month === 12) {
            month = 0;
            year++;
        }
        this.monthDate = this.toDate(year, month, 1);
        this.detectChanges();
    }

    /**
     * Shifts calendar one month back
     */
    prevMonth() {
        let month = this._monthDate.getMonth() - 1;
        let year = this._monthDate.getFullYear();
        if (month === -1) {
            month = 11;
            year--;
        }
        this.monthDate = this.toDate(year, month, 1);
        this.detectChanges();
    }

    /**
     * Shifts calendar one year ahead
     */
    nextYear(): void {
        this.year = this.monthDate.getFullYear() + 1;
    }

    /**
     * Shifts calendar one year back
     */
    prevYear(): void {
        this.year = this.monthDate.getFullYear() - 1;
    }

    /**
     * Pick given date or unselect
     */
    protected pick(day?: Date): void {
        if (day && this.isDisabled(day)) {
            return;
        }
        if (this.setModelAsDate(day)) {
            this.modelChange.emit(this.model);
            this.detectChanges();
        }
    }

    private setModelAsDate(date: Date | undefined) {
        const value = isValidDate(date) ? date : undefined;
        if (this.dateModel?.getTime() !== value?.getTime()) {
            this.dateModel = value;
            return true;
        }
        return false;
    }

    /**
     * Checks if given date is currently picked one
     */
    protected isPicked(day: Date): boolean {
        // we check yearh month and day cause there may be difference in time
        return this.dateModel ? isSameDay(day, this.dateModel) : false;
    }

    /**
     * Checks if given date is today date
     * @param {Date} day
     */
    protected isToday(day: Date): boolean {
        // we check yearh month and day cause there may be difference in time
        return isSameDay(day, this.today);
    }

    /**
     * Checks if date is not valid (and is disabled)
     */
    protected isDisabled(day: Date): boolean {
        if (!day) {
            return true;
        }
        if ((this.minDate && this.minDate > day) || (this.maxDate && this.maxDate < day)) {
            return true;
        }
        if (this.options.validate && !this.options.validate(day)) {
            return true;
        }

        return false;
    }

    /**
     * Tracking function for days
     */
    protected indexTrackFn(index: number) {
        return index;
    }

    /**
     * Updates this.weekDays - array of Date objects of which first is
     * proper day of a week following firstDayOfWeek setting
     *
     * You use it to show header line with day of week names
     * (use date filter: | date:'EEE' to extract day of week)
     *
     * We keep it as date for better display flexibility
     */
    private setWeekDays(): void {
        const firstWeekDate = new Date();
        const firstDayOfWeek = this.options.firstDayOfWeek || 0;
        while (firstWeekDate.getDay() !== firstDayOfWeek) {
            firstWeekDate.setDate(firstWeekDate.getDate() + 1);
        }

        this.weekDays = range(7).map(i => {
            const weekDay = new Date(firstWeekDate.getTime());
            weekDay.setDate(weekDay.getDate() + i);
            return weekDay;
        });
    }

    /**
     * Takes all days for given month
     * @param {number} month Month 0-11
     * @param {number} year  Year
     * @return {Date[]} Dates array (ordered)
     */
    private getDaysInMonth(month: number, year: number): Date[] {
        const date = this.toDate(year, month, 1);
        const days: Date[] = [];
        while (date.getMonth() === month) {
            days.push(new Date(date.getTime()));
            date.setDate(date.getDate() + 1);
            days.push;
        }
        return days;
    }

    private getWeeks(): Date[][] {
        const firstDayOfMonthDayOfWeek = (<Date>head(this.days)).getDay();
        const daysShift = firstDayOfMonthDayOfWeek - (this.options.firstDayOfWeek || 0);
        const weeks: (Date | null)[][] = values(groupBy(this.days, day => 10 + Math.floor((day.getDate() - 1 + daysShift) / 7)));
        // -1 cause first day is 1st not 0; adding 10 to avoid negative numbers to keep order

        this.addMissingDates(head(weeks)!, 'first');
        this.addMissingDates(last(weeks)!, 'last');
        return weeks as Date[][];
    }

    private addMissingDates(week: (Date | null)[], type: 'first' | 'last'): void {
        const missingDays = 7 - week.length;
        if (missingDays) {
            const edgeDate = type === 'first' ? head(week) : last(week);
            if (edgeDate) {
                for (let index = 0; index < missingDays; index++) {
                    let value: (Date & { otherMonth?: boolean }) | null = null;

                    if (this.options?.displayOtherMonthsDates) {
                        const time = edgeDate.getTime();
                        value = new Date(time + (type === 'first' ? -1 : 1) * ((index + 1) * 86400000));
                        value.otherMonth = true;
                    }
                    if (type === 'first') {
                        week.unshift(value);
                    } else {
                        week.push(value);
                    }
                }
            }
        }
    }

    /**
     * Applies local date selection to model (if valid)
     * or undefined otherwise
     */
    private applySelection() {
        if (this.initiated && !this.preventModelEmit) {
            this.modelChange.emit(this.model);
            this.detectChanges();
        }
    }

    /**
     * Initialize monthDate and year
     */
    private resetMonthDate() {
        let source: Date | undefined = [this.dateModel, this.monthDate].find(isValidDate);
        if (!source) {
            // try to get first valid date
            source = new Date();
            let sourceCopy = new Date(source.getTime());
            let i = 0;
            const limit = 150;
            while (i < limit && this.isDisabled(sourceCopy)) {
                i++;
                sourceCopy.setDate(sourceCopy.getDate() + 1);
                this.pick(sourceCopy);
            }
            if (!this.isDisabled(sourceCopy)) {
                source = sourceCopy;
            }
        }
        const year = source.getFullYear();
        const month = source.getMonth();
        this.monthDate = this.toDate(year, month, 1);
    }

    /**
     * Initialize options including defaults
     * sanitize any problematic things
     */
    private sanitizeOptions() {
        // prepare default options
        this.options = defaults(this.options || {}, this.defaultOptions);

        // make sure first day is in 0-6 range
        this.options.firstDayOfWeek = Math.max(Math.min(this.options.firstDayOfWeek || 0, 6), 0);

        // normalize min/max
        this.minDate = typeof this.options.min === 'string' ? this.stringToDate(this.options.min) : this.options.min;
        this.maxDate = typeof this.options.max === 'string' ? this.stringToDate(this.options.max) : this.options.max;
    }

    /**
     * Converts string to date
     * @param dateString Iso date string (like 2020-01-01)
     */
    private stringToDate(dateString: string | Date | undefined): Date | undefined {
        if (dateString instanceof Date) {
            return dateString;
        }
        if (typeof dateString !== 'string') {
            return undefined;
        }
        const parts = dateString.split('T')[0].split('-');
        return this.toDate(+parts[0], +parts[1] - 1, +parts[2]);
    }

    private toDate(year: number, month: number, day: number) {
        return new Date(Date.UTC(year, month, day, 0, 0, 0));
    }

    /**
     * Converts date to string
     * @param date Date object
     * @returns ISO date string (like 2020-01-01)
     */
    private dateToString(date: Date): string {
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }
}
